import GetRequest from "csi/comm/GetRequest";
import { cnl } from "csi/typings";
import Cookie from "js-cookie";
import * as api from "../api";
import { MSTEAMS_MODULE } from "./component/MSTeamsStore";

/** APIs */
export enum Operations {
	ASK_PERMISSION = "AskMSTeamsPermissions",
	GET_PUBLIC_CHANNELS = "GetListOfPublicChannels",
	GET_PRIVATE_CHANNELS = "GetListOfPrivateChannels",
	GET_CHANNEL_DETAILS = "GetChannelDetails",
	GET_TEAMS = "GetListOfTeams",
	CREATE_CHANNEL = "CreateChannel",
	GET_USER_PROFILE_PHOTO = "GetUserProfilePhoto",
	GET_USERS = "GetUsers",
	GET_CHANNEL_MESSAGES = "GetChannelMessages",
	GET_HOSTED_CONTENT = "GetHostedContent",
	POST_MESSAGE = "PostMessageToChannel",
	SUBSCRIBE_CHANNEL = "SubscribeChannel",
}

export enum ChatMessageAttachmentContentType {
	ANNOUNCEMENT = "application/vnd.microsoft.card.announcement",
	CODE_SNIPPET = "application/vnd.microsoft.card.codesnippet",
	REFERENCE = "reference",
	THUMBNAIL = "application/vnd.microsoft.card.thumbnail",
}

export enum ChatMessageType {
	MESSAGE = "MESSAGE",
	CHAT_EVENT = "CHAT_EVENT",
	SYSTEM_EVENT_MESSAGE = "SYSTEM_EVENT_MESSAGE",
	TYPING = "TYPING",
	UNKNOWN_FUTURE_VALUE = "UNKNOWN_FUTURE_VALUE",
}

export interface Identity {
	// https://docs.microsoft.com/en-us/graph/api/resources/identity?view=graph-rest-1.0
	displayName: string;
	id: string;
}

export interface ChatMessageFromIdentitySet {
	// https://docs.microsoft.com/en-us/graph/api/resources/chatmessagefromidentityset?view=graph-rest-1.0
	application: Identity;
	user: Identity;
}

export interface ItemBody {
	content: string;
	contentType: "TEXT" | "HTML";
}

export interface MSTeamsApiResponse {
	ok: boolean;
	error?: string;
}

interface MSTeamsErrorResponse {
	/** localized error message */
	ErrorAdmin?: string;

	/** full exception including trace stack */
	Exception?: string;

	/** potential error messages if any */
	Param?: string[];
}

type MembershipType = api.ValueOf<typeof ChannelMembershipType>;

/** fix the display name */
export function fixDisplayName(userId: string, userName: string, userDictionary: User[]): string {
	// if somebody sets up a meeting, the user name would have this pattern
	if (userName.includes("orgid:")) {
		userName = userDictionary?.find((x) => x.userId === userId)?.displayName;
	}

	return userName;
}

export class MSTeamsApiError extends Error {
	constructor(public apiResponse?: MSTeamsApiResponse & MSTeamsErrorResponse) {
		super(apiResponse.error || apiResponse.Exception);
	}
}

export enum ChannelMembershipType {
	//https://docs.microsoft.com/en-us/graph/api/resources/enums?view=graph-rest-1.0#channelmembershiptype-values
	standard = "STANDARD",
	private = "PRIVATE",
	unknown = "UNKNOWN",
}

export enum TeamVisibilityType {
	//https://docs.microsoft.com/en-us/graph/api/resources/teamvisibilitytype?view=graph-rest-1.0
	private,
	public,
}

export interface Reaction {
	createdDateTime: number;
	reactionType: string;
}

export interface Attachment {
	// https://docs.microsoft.com/en-us/graph/api/resources/attachment?view=graph-rest-1.0
	content?: string;
	contentType: ChatMessageAttachmentContentType;
	contentUrl?: string;
	id: string;
	name?: string;
}

export type ReactionMap = Record<string, number>;

export interface ChatMessage {
	// https://docs.microsoft.com/en-us/graph/api/resources/chatmessage?view=graph-rest-1.0
	attachments: Attachment[];
	body: ItemBody;
	createdDateTime: number;
	etag: string;
	from: ChatMessageFromIdentitySet;
	id: string;
	importance: "NORMAL" | "HIGH" | "URGENT";
	lastModifiedDateTime: number;
	messageType: ChatMessageType;
	reactions?: Reaction[];
	replies?: ChatMessageCollectionPage;
	subject?: string;
}

export interface ChatMessageCollectionPage {
	count: number;
	currentPage: ChatMessage[];
	nextPage?: {
		client: any;
		requestUrl: string;
	};
}

export interface Channel extends api.Channel {
	//https://docs.microsoft.com/en-us/graph/api/resources/channel?view=graph-rest-1.0 
	description: string;
	displayName: string;
	email: string;
	id: string;
	membershipType: MembershipType;
	webUrl: string;

	//#region custom properties 

	/** indicate if has name violation */
	channelNameViolation?: boolean;

	//#endregion
}

export interface ConversationMember extends api.User {
	//https://docs.microsoft.com/en-us/graph/api/resources/conversationmember?view=graph-rest-1.0
	displayName: string;
	roles: string[];
}

export interface Team extends api.Team {
	//https://docs.microsoft.com/en-us/graph/api/resources/team?view=graph-rest-1.0
	displayName: string;
	description: string;
	id: string;
	isArchived: boolean;
	members: ConversationMember;
	visibility: TeamVisibilityType;
}

export interface User extends ConversationMember {
	//https://docs.microsoft.com/en-us/graph/api/resources/aaduserconversationmember?view=graph-rest-1.0
	email: string;
	userId: string;
}

/** get list of public channels which the current user is part of */
export async function getListOfPublicChannels(teamIds: string[]): Promise<Channel[]> {
	const formData = new FormData();
	teamIds.forEach((teamId) => formData.append("TeamIds", teamId));

	return msteamsOperationPost<Channel[]>(Operations.GET_PUBLIC_CHANNELS, formData);
}

/** get list of private channels which the current user is part of */
export async function getListOfPrivateChannels(teamIds: string[]): Promise<Channel[]> {
	const formData = new FormData();
	teamIds.forEach((teamId) => formData.append("TeamIds", teamId));

	return msteamsOperationPost<Channel[]>(Operations.GET_PRIVATE_CHANNELS, formData);
}

/** get the details of channels */
export async function getChannelDetails(c8Instance: cnl, teamIds: string[], channelIds: string[]): Promise<Channel[]> {
	const formData = new FormData();
	formData.append("C8InstanceURL", c8Instance);
	teamIds.forEach((teamId) => formData.append("TeamIds", teamId));
	channelIds.forEach((channelId) => formData.append("ChannelIds", channelId));

	return msteamsOperationPost<Channel[]>(Operations.GET_CHANNEL_DETAILS, formData);
}

/** get list of messages of particular channel */
export async function getListOfMessagesOfChannel(teamId: string, channelId: string, messageId: string = "", pageSize: string = "20", skiptoken: string = ""): Promise<ChatMessageCollectionPage> {
	const formData = new FormData();
	formData.append("TeamId", teamId);
	formData.append("ChannelId", channelId);
	formData.append("MessageId", messageId);
	formData.append("PageSize", pageSize);
	formData.append("SkipToken", skiptoken);

	return msteamsOperationPost<ChatMessageCollectionPage>(Operations.GET_CHANNEL_MESSAGES, formData);
}

/** get list of messages of particular channel */
export async function getUserProfilePhoto(userId: string): Promise<string> {
	const formData = new FormData();
	formData.append("UserId", userId);

	return msteamsOperationPost<string>(Operations.GET_USER_PROFILE_PHOTO, formData);
}

/** get list of hosted content of particular channel */
// TODO in another story
// export async function getListOfHostedContent(teamId: string, channelId: string, messageId: string): Promise<any[]> {
// 	const formData = new FormData();
// 	formData.append("TeamId", teamId);
// 	formData.append("ChannelId", channelId);
// 	formData.append("MessageId", messageId);

// 	return msteamsOperationPost<any[]>(Operations.GET_HOSTED_CONTENT, formData);
// }

/** get list of teams which the current user is part of */
export async function getListOfTeams(): Promise<Team[]> {
	return msteamsOperationGet<Team>(Operations.GET_TEAMS);
}

/** fetch the original users from server, either team level or channel level */
export async function getUsers(teamId: string, channelId: string = ""): Promise<User[]> {
	const formData = new FormData();
	formData.append("TeamId", teamId);
	formData.append("ChannelId", channelId);
	return msteamsOperationPost<User[]>(Operations.GET_USERS, formData);
}

/** create public/private channel w/ members */
export async function createChannel(
	teamId: string,
	channelName: string,
	description: string,
	isPrivate: boolean,
	memberIds?: string[],
): Promise<Channel> {
	const formData = new FormData();
	formData.append("TeamId", teamId);
	formData.append("DisplayName", channelName);
	formData.append("Description", description);
	formData.append("IsPrivate", String(isPrivate));
	memberIds?.forEach((userId) => formData.append("MemberIds", userId));

	return msteamsOperationPost<Channel>(Operations.CREATE_CHANNEL, formData);
}

/** post message to particular channel */
export async function postMessage(teamId: string, channelId: string, messageId: string = "", content: string, withHighPriority: boolean, urlToUnfurl?: string, memberIds?: string[]): Promise<ChatMessage> {
	const formData = new FormData();
	formData.append("TeamId", teamId);
	formData.append("ChannelId", channelId);
	formData.append("MessageId", messageId);
	formData.append("MessageContent", content);
	formData.append("MessageImportance", String(withHighPriority));
	formData.append("Url", urlToUnfurl);
	memberIds?.forEach((userId) => formData.append("MemberIds", userId));

	return msteamsOperationPost<ChatMessage>(Operations.POST_MESSAGE, formData);
}

/** subscribe a particular channel to be able to get the notification */
export async function subscribeChannel(teamId: string, channelId: string): Promise<boolean> {
	const formData = new FormData();
	formData.append("TeamId", teamId);
	formData.append("ChannelId", channelId);

	return msteamsOperationPost<boolean>(Operations.SUBSCRIBE_CHANNEL, formData);
}

//----------------------------------------------------------------------------------------------------
//                   Utility functions
//----------------------------------------------------------------------------------------------------

/** build the request and fetch the results */
async function msteamsOperationGet<T>(operationName: string, parameters?: Record<string, string>): Promise<T[]> {
	const getRequest = new GetRequest(MSTEAMS_MODULE, operationName);

	if (parameters) {
		for (const [key, value] of Object.entries(parameters)) {
			getRequest.addParameter(key, value);
		}
	}

	const response = await fetch(getRequest.toRequest());
	checkResponse(response);

	const json = await response.json();
	checkJson(json);
	return json;
}

/** build the post request and fetch the results */
async function msteamsOperationPost<T>(operationName: string, formData: FormData): Promise<T> {
	const requestUrl = new GetRequest(MSTEAMS_MODULE, operationName)
		.toRequest();

	const response = await fetch(requestUrl, {
		method: "POST",
		body: formData,
		headers: {
			"X-CSRF-Token": Cookie.get("CSRFToken"),
		},
	});

	checkResponse(response);

	const json = await response.json();
	checkJson(json);

	return json;
}

/** check the response  */
function checkResponse(response: MSTeamsApiResponse): void {
	if (!response.ok) {
		throw new MSTeamsApiError(response);
	}
}

/** check the content of json response */
function checkJson(json: any): void {
	if (json.Exception) {
		// skip this case and assign a custom property
		if (checkChannelNameExists(json)) {
			json.channelNameViolation = true;
			return;
		}

		throw new MSTeamsApiError(json);
	}
}

export interface Reaction {
	name: string;
	count: number;
	users: string[];
	url: string;
}

export interface MessageGroup {
	date: Date;
	dateAsString: string;
	groupTitle: string;
	messages: ChatMessage[];
}

export interface ConversationHistory {
	messages: ChatMessage[];
	responseMetadata?: any;
}

/** mutate rich text for posting a message to ms teams */
export function mutateRichTextForPost(htmlString: string): string {
	const el = document.createElement("html");
	el.innerHTML = htmlString;

	// update the index for each mention
	const nodeList: HTMLCollectionOf<Element> = el.getElementsByTagName("AT");
	Array.from(nodeList)
		.forEach((mentionElement, i) => {
			mentionElement.id = i.toString();
		});

	// add inline table styling that matches ms teams so tables are visible in ms teams
	el.querySelectorAll("td")
		.forEach((td) => {
			const { style } = td;
			style.borderStyle = "solid";
			style.borderWidth = "1px";
		});

	return el.innerHTML;
}

/** check if it's the exception of channel name exist */
export function checkChannelNameExists(error: MSTeamsErrorResponse): boolean {
	return error.Param?.includes("Channel name already existed, please use other name");
}